<template>
  <div>
    <div v-if="isLoading">
      <v-skeleton-loader type="table"></v-skeleton-loader>
    </div>
    <v-data-table
      v-if="!isLoading"
      v-model="selectedData"
      :loading="loading"
      :headers.sync="headersDataFiltered"
      :items="itemsData"
      class="v-data-table table1 mb-6 colorBadge tableBackgroundColor"
      :class="[tableId, elevation]"
      :key="componentKey"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :multi-sort="multiSort"
      :show-select="showSelect"
      :item-key="itemKey"
      :hide-default-footer="
        hideDefaultFooter || (itemsData.length <= 10 && serverItemsLength <= 10)
      "
      :mobile-breakpoint="0"
      :server-items-length="serverItemsLength"
      :items-per-page="itemsPerPage"
      :page="page"
      :footer-props="{
        itemsPerPageOptions: itemsPerPageOptions,
      }"
      :disable-sort="disableSort"
      @update:sort-by="updateSortBy"
      @update:sort-desc="updateSortDesc"
      @update:page="updatePage"
      @update:items-per-page="updateItemsPerPage"
    >
      <template v-slot:top v-if="!hideHeader">
        <v-toolbar class="tableBackgroundColor" flat>
          <TableHeaderTitle
            v-if="title || showTitleHeader"
            :tableId="tableId"
            :header.sync="headers"
            :title="title"
            :items="itemsDataFiltered"
            :export-name="exportName"
            :removeExportButton="removeExportButton"
            :buttons="buttons"
            :hideRemoveFilterButton="hideRemoveFilterButton"
            @returnBtn="returnBtn"
          />
        </v-toolbar>
      </template>

      <template v-slot:header.data-table-select="{ on, props }">
        <v-simple-checkbox
          v-if="!hideSelectAll"
          class="NotDraggable"
          color="green"
          v-bind="props"
          v-on="on"
          :ripple="false"
        ></v-simple-checkbox>
      </template>

      <template v-slot:body.prepend="{ headers }" v-if="!hideFilter">
        <tr>
          <td
            v-for="(head, index) in headers"
            :key="index"
            :style="{ 'min-width': '120px' }"
          >
            <slot name="filter" :item="itemsData" :head="head">
              <slot
                :name="'filter.' + head.value"
                :items="itemsData"
                :head="head"
                :headers="headers"
              >
                <BaseTableSearchField
                  v-if="head && !head.removeFilter"
                  v-model="head.filterValues"
                  :filterClause.sync="head.filterClause"
                  :type="head.type"
                  :items="itemsData"
                  :selectData="head.value"
                  :headers="headers"
                  @filter="filter"
                />
              </slot>
            </slot>
          </td>
        </tr>
      </template>

      <template v-slot:item="{ index, item, headers, isSelected, select }">
        <tr>
          <td
            :class="{ pointer: clickableRow }"
            v-for="(head, index) in headers"
            :key="index"
            @click="rowClicked(item)"
          >
            <v-simple-checkbox
              v-if="head.value === 'data-table-select' && !item.hideCheckbox"
              color="green"
              :value="isSelected"
              :ripple="false"
              @input="select($event)"
            ></v-simple-checkbox>
            <slot v-else name="item" :item="item" :head="head">
              <slot :name="'item.' + head.value" :item="item" :head="head">
                <span v-if="head.type === 'boolean'">
                  {{ item[head.value] ? "Ja" : "Nei" }}
                </span>
                <span v-else>
                  <span v-if="head.type === 'date' && item[head.value]">
                    {{ item[head.value] | moment(getDateType(head.dateType)) }}
                  </span>
                  <span v-else>{{ item[head.value] }}</span>
                </span>
              </slot>
            </slot>
          </td>
        </tr>
      </template>

      <template v-if="showAppend" v-slot:body.append="{ items, headers }">
        <tr>
          <td v-for="(head, index) in headers" :key="index">
            <slot
              :name="'append.' + head.value"
              :items="items"
              :head="head"
            ></slot>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import BaseTableSearchField from "@/components/shared/table/BaseTableSearchField";
import TableHeaderTitle from "@/components/shared/table/TableHeaderTitle";

import filter from "@/helpers/filter.ts";

export default {
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    items: {
      required: true,
    },
    headers: {
      required: true,
    },
    tableId: {
      default: "",
    },
    multiSort: {
      type: Boolean,
      default: true,
    },
    disableSort: {
      type: Boolean,
      default: false,
    },
    showSelect: {
      type: Boolean,
      default: false,
    },
    hideSelectAll: {
      type: Boolean,
      default: false,
    },
    itemKey: {
      default: "id",
    },
    hideDefaultFooter: {
      type: Boolean,
      default: false,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    hideFilter: {
      type: Boolean,
      default: false,
    },
    exportName: {
      default: null,
    },
    clickableRow: {
      type: Boolean,
      default: false,
    },
    totalPages: {
      type: Number,
    },
    serverItemsLength: {
      default: -1,
    },
    itemsPerPage: {
      default: 10,
    },
    itemsPerPageOptions: {
      default: () => [5, 10, 15, -1],
    },
    buttons: {
      default: () => [],
    },
    elevation: {
      default: "elevation-1",
    },
    showAppend: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    sortBy: {
      type: Array,
      default: () => [],
    },
    sortDesc: {
      type: Array,
      default: () => [],
    },
    removeExportButton: {
      type: Boolean,
      default: false,
    },
    hideRemoveFilterButton: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    componentKey: 0,
    page: 1,
  }),

  methods: {
    filter(data) {
      this.$emit("filter", data);
    },
    filterClause(index, value, headers) {
      headers[index].filterClause = value;
    },
    //Gui filter
    filterType(value, filterValues, type, filterClause) {
      //Filter inside table
      return filter.filterTypeTable(value, filterValues, type, filterClause);
    },

    rowClicked(item) {
      this.$emit("rowClicked", item);
    },
    getDateType(dateType) {
      if (dateType) {
        return dateType;
      } else {
        return "DD-MM-YYYY HH:mm";
      }
    },
    updateSortBy(sortBy) {
      if (sortBy[0]) {
        var data = {
          sortBy: sortBy && sortBy[0] ? sortBy[0] : this.sortBy[0],
          sortDesc: this.sortDesc[0],
        };
        this.$emit("updateSortBy", data);
      }
    },
    updateSortDesc(sortDesc) {
      if (sortDesc[0] !== undefined) {
        let data = {
          sortBy: this.sortBy[0],
          sortDesc:
            sortDesc && sortDesc[0] !== undefined
              ? sortDesc[0]
              : this.sortDesc[0],
        };
        this.$emit("updateSortBy", data);
      } else {
        let data = {
          sortBy: this.sortBy[0],
          sortDesc: !this.sortDesc[0],
        };
        this.$emit("updateSortBy", data);
      }
    },
    updatePage(pageNumber) {
      this.$emit("newPage", pageNumber);
    },
    updateItemsPerPage(ItemsPerPage) {
      this.$emit("updateItemsPerPage", ItemsPerPage);
    },
    returnBtn(data) {
      this.$emit(data.emitName, data);
    },
  },
  watch: {
    totalPages(value) {
      if (this.page > value) {
        this.page = value >= 1 ? value : 1;
      }
    },
  },

  computed: {
    selectedData: {
      get: function () {
        return this.value;
      },
      set: function (value) {
        this.$emit("input", value);
      },
    },
    itemsData: {
      get: function () {
        return this.items;
      },
      set: function (value) {
        this.$emit("update:items", value);
      },
    },
    itemsDataFiltered() {
      if (this.headersData) {
        var headers = this.headersData;
        var data = this.items.filter((x) => {
          return headers.every((y) => {
            if (y.filterValues) {
              return filter.filterTypeTable(
                x[y.filterName],
                y.filterValues,
                y.type,
                y.filterClause
              );
            }
            return true;
          });
        });
        return data;
      }

      return this.items;
    },
    headersDataFiltered: {
      get() {
        return this.headers.filter((x) => x.show);
      },
    },
    isLoading() {
      const isLoadingItems = !this.items;

      return isLoadingItems ? true : false;
    },
    title() {
      if (this.headerTitle) {
        return this.headerTitle;
      }
      return this.$route.meta?.headerName;
    },
  },
  components: {
    BaseTableSearchField,
    TableHeaderTitle,
  },
};
</script>

<style lang="scss" scoped>
.pointer {
  cursor: pointer;
}
</style>

<style lang="scss">
.tableBackgroundColor {
  background-color: var(--v-cardSurface-base) !important;
}

.colorBadge {
  .v-data-table-header__sort-badge {
    background-color: rgb(255, 117, 24) !important;
    color: rgba(255, 255, 255, 0.87) !important;
  }
}
</style>
